<template>
    <div
        class="upgradePlanItem"
        :class="{
            'upgradePlanItem__disabled': disabledCurrentPlan,
            'upgradePlanItem__lastOption': lastOption,
            'upgradePlanItem__cursor': !showButton,
            'upgradePlanItem__active': !hasCustom,
        }"
        @click="handleSelectItem"
    >
        <div :class="classesPlan">
            <span>
                {{ titlePlan }}
            </span>
        </div>

        <div class="upgradePlanItem__price">
            <template v-if="hasCustom">
                <span>{{ customPriceLable }}</span>
            </template>
            <template v-else>
                <span>${{ currentPrice }}</span>
                <span>/{{ prefixPeriod }}</span>
            </template>
        </div>

        <div class="upgradePlanItem__countMessage">
            {{ getCountTextMsg }} {{ priceLabel }}
        </div>

        <div class="upgradePlanItem__feature">
            <span
                v-for="(item, index) in features"
                :key="index"
            >
                {{ item }}
            </span>
            <small
                v-if="checkPlanForNote"
                class="upgradePlanItem__note"
            >
                <sup>*</sup>Billed annually
            </small>
        </div>

        <div
            v-if="showButton"
            class="upgradePlanItem__button"
        >
            <a
                :id="isChatWithSales ? 'intercom-custom-plan_modal-button' : null"
                :class="classesButton"
                href="#"
                @click="handleUpgrade($event)"
            >
                {{ textButton }}
            </a>
        </div>
    </div>
</template>

<script>
import Form from 'core/Form';
import OrganizationMixin from 'mixins/organization';
import CardsMixin from 'mixins/cards';

export default {
    emits: ['handleChangeDataUpgrade', 'handleChangeData', 'close'],
    
    mixins: [
        OrganizationMixin,
        CardsMixin,
    ],

    props: {
        currentStep: { type: Number, default: 0 },
        planList: { type: Array },
        plan: { type: String, default: '' },
        type: { type: String, default: '' },
        period: { type: String, default: '' },
        planCredit: { type: Number, default: null },
        defaultTarget: { type: Boolean, default: false },
        currentTarget: { type: String, default: '' },
        defaultPlanName: { type: String, default: '' },
        lastOption: { type: Boolean, default: false },
        showButton: { type: Boolean, default: true },
    },

    data() {
        return {
            form: new Form({
                interval: 'yearly',
                token: '',
                primary: true,
                backup: false,
                amount: 0,
                auto_recharge: false,
                plan: '',
            }, false),
        };
    },

    computed: {
        hasCustom() {
            return this.type.includes('custom');
        },

        hasPro() {
            return this.type.includes('pro-plan');
        },

        classesPlan() {
            return {
                'upgradePlanItem__plan': true,
                'upgradePlanItem__plan--styleOne': this.hasPro,
                'upgradePlanItem__plan--styleTwo': this.hasCustom,
            };
        },

        titlePlan() {
            if (this.type === 'pro-plan-monthly') {
                return 'Pro';
            }

            if (this.type === 'custom-monthly') {
                return 'Custom';
            }

            if (this.type === 'pro-plan-annual') {
                return 'Pro';
            }

            return 'Custom';
        },

        customPriceLable() {
            return this.lastOption ? 'Custom Plan' : 'Get In Touch';
        },

        prefixPeriod() {
            if (this.period === 'monthly') {
                return 'mo';
            }

            return 'mo*';
        },

        priceLabel() {
            if (this.lastOption) {
                return '';
            }

            if (this.hasCustom) {
                return 'Text Messages';
            }

            if (this.period === 'monthly') {
                return 'credits per month';
            }

            return 'credits per year';
        },

        currentCredit() {
            if (this.currentTarget === 'annual') {
                return this.planList[this.currentStep].credits / 12;
            }

            return this.planList[this.currentStep].credits;
        },

        currentCreditText() {
            if (this.planList[this.currentStep].credits < 1000) {
                return this.planList[this.currentStep].credits;
            } else {
                return `${this.planList[this.currentStep].credits / 1000}k`;
            }
        },

        currentPrice() {
            if (this.lastOption) {
                return 'Custom Plan';
            } else if (this.type === 'custom-monthly' || this.type === 'custom-annual') {
                return 'Get In Touch';
            } else if (this.currentTarget === 'annual') {
                return Math.round(this.planList[this.currentStep].price) / 12;
            } else {
                return Math.round(this.planList[this.currentStep].price);
            }
        },

        compareDefaultCreditWithCurrent() {
            if (!this.checkTrial) {
                return this.defaultTarget && this.currentCredit === this.planCredit;
            }

            return false;
        },

        features() {
            if (this.type === 'pro-plan-monthly' || this.type === 'pro-plan-annual') {
                return [
                    `$${(this.planList[this.currentStep].additional_credit_amount / 100).toFixed(4)} per additional credit`,
                    'Unlimited contacts',
                    'Unlimited app integrations',
                ];
            }

            if (this.type === 'custom-monthly' || this.type === 'custom-annual') {
                return [
                    'Talk to a member of our',
                    'enterprise sales team',
                    'about your specific needs',
                ];
            }

            return [];
        },

        classesButton() {
            return {
                'button': true,
                'is-primary': true,
                'is-disabled': this.disabledButton,
                'upgradePlanItem__button-disabled': this.disabledCurrentPlan,
                'button__custom': this.hasCustom,
            };
        },

        disabledButton() {
            return false;
        },

        getCountTextMsg() {
            if (this.lastOption) {
                const creditsText = Number(this.planList[this.currentStep].credits).toLocaleString('en-US', {
                    maximumFractionDigits: 2
                });

                if (this.period === 'monthly') return `Need more then ${creditsText} messages per month?`;
                if (this.period === 'annual') return `Need more then ${creditsText} messages per year?`;
            }

            if (this.hasCustom) {
                if (this.period === 'annual') return 'Over 600,000';

                return 'Over 50,000';
            }

            return this.currentCreditText;
        },

        checkTrial() {
            return this.subscription.status === 'trialing';
        },

        checkPlanForNote() {
            return this.hasPro && this.period === 'annual';
        },

        availableCanceledStatus() {
            return !this.isCancelled && !this.checkOldBillingPlans && this.compareDefaultCreditWithCurrent;
        },

        disabledCurrentPlan() {
            return !this.isCanceledOrTrial && !this.checkOldBillingPlans && this.compareDefaultCreditWithCurrent && !this.lastOption;
        },

        hasPlanDowngrade() {
            return Boolean(
                !this.isCanceledOrTrial &&
                    !this.checkPlanFree &&
                    !this.lastOption &&
                    (
                        (!this.defaultTarget && this.currentTarget === 'monthly') ||
                        (this.defaultTarget && this.currentCredit < this.getDefaultPlanCredit)
                    )
            );
        },

        isChatWithSales() {
            return !this.hasPlanDowngrade && (this.hasCustom || this.lastOption);
        },

        textButton() {
            if (this.hasPlanDowngrade) {
                return 'Downgrade';
            } else if (this.hasCustom || this.lastOption) {
                return 'Chat With Sales';
            } else if (this.disabledCurrentPlan) {
                return 'Current plan';
            } else {
                return 'Upgrade';
            }
        }
    },

    watch: {
        currentStep(value) {
            this.handleSelectItem();
        }
    },

    methods: {
        async handleUpgrade(e) {
            e && e.preventDefault();

            if (this.disabledButton || this.hasCustom) return;

            this.$emit('handleChangeDataUpgrade', ['plan', this.plan]);
            this.$emit('handleChangeDataUpgrade', ['type', this.type]);
            this.$emit('handleChangeDataUpgrade', ['price', this.currentPrice]);
            this.$emit('handleChangeData', ['hasPlanDowngrade', this.hasPlanDowngrade]);
            this.$emit('handleChangeData', ['showPreviewPlan', true]);

            if (!this.sortedBillingSources.length || this.isCancelled) {
                this.$emit('handleChangeDataUpgrade', ['hasCard', false]);
            } else {
                this.$emit('handleChangeDataUpgrade', ['hasCard', true]);
            }
        },

        handleSelectItem(e) {
            if (this.showButton) return;

            this.handleUpgrade(e);
        },

        close() {
            this.$emit('close');
        },

        async handlePreselectItem() {
            await this.$delay(200);
            this.handleSelectItem();
        }
    },

    mounted() {
        this.handlePreselectItem();
    }
};
</script>
